import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { getService } from "../../data/services";
import { Progress } from "./Progress";
import { Summary, MobileSummary } from "./Summary";
import { StepService } from "./steps/StepService";
import { StepStyle } from "./steps/StepStyle";
import { StepAppointment } from "./steps/StepAppointment";
import { StepDetails } from "./steps/StepDetails";
import { StepIntake } from "./steps/StepIntake";
import { StepPolicies } from "./steps/StepPolicies";
import { StepDeposit } from "./steps/StepDeposit";
import { StepConfirmation } from "./steps/StepConfirmation";

const STEPS = [
  "Service",
  "Style",
  "Appointment",
  "Details",
  "Intake",
  "Policies",
  "Deposit",
];

const STEP_COMPONENTS = [
  StepService,
  StepStyle,
  StepAppointment,
  StepDetails,
  StepIntake,
  StepPolicies,
  StepDeposit,
];

export const PrototypeBookingFlow = () => {
  const [params] = useSearchParams();
  const preselect = params.get("service");
  const [form, setForm] = useState({
    serviceId: getService(preselect) ? preselect : "",
    size: "",
    length: "",
    color: "",
    inspo: null,
    date: "",
    time: "",
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    instagram: "",
    hairType: "",
    hairLength: "",
    notes: "",
    agreed: false,
  });
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [tried, setTried] = useState(false);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const phoneOk = form.phone.replace(/\D/g, "").length >= 10;
  const valid = [
    !!form.serviceId,
    !!(form.size && form.length && form.color),
    !!(form.date && form.time),
    !!(form.firstName.trim() && form.lastName.trim() && phoneOk && emailOk),
    !!(form.hairType && form.hairLength),
    form.agreed,
    true,
  ][step];

  const next = () => {
    if (!valid) {
      setTried(true);
      return;
    }
    setTried(false);
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: "instant" });
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  if (done) return <StepConfirmation form={form} />;

  const StepEl = STEP_COMPONENTS[step];

  return (
    <div className="mx-auto max-w-6xl" data-testid="prototype-booking-flow">
      <MobileSummary form={form} />
      <div className="mt-6 grid gap-10 lg:grid-cols-[1fr_340px]">
        <div>
          <Progress steps={STEPS} current={step} />
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
              <StepEl form={form} set={set} onDone={() => setDone(true)} />
            </motion.div>
          </AnimatePresence>

          {tried && !valid && (
            <p className="mt-4 text-sm font-semibold text-hotpink" role="alert" data-testid="booking-validation-error">
              Please complete the required selections above to continue.
            </p>
          )}

          <div className="mt-10 flex items-center justify-between gap-4">
            {step > 0 ? (
              <button
                type="button"
                onClick={back}
                data-testid="booking-back-button"
                className="flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] text-white/70 transition-colors duration-300 hover:border-white hover:text-white"
              >
                <ChevronLeft className="h-4 w-4" /> Back
              </button>
            ) : (
              <span />
            )}
            {step < STEPS.length - 1 && (
              <button
                type="button"
                onClick={next}
                data-testid="booking-continue-button"
                className="flex items-center gap-2 rounded-full bg-hotpink px-8 py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-magenta"
              >
                Continue <ChevronRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        <Summary form={form} />
      </div>
    </div>
  );
};

